import {
  type MasterChecklistItem,
  type MasterChecklistResponse,
  masterChecklistResponseSchema,
} from '@sivan/shared';
import { useEffect, useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost } from '../api/client';
import { LoadingBlock, Problem } from '../handover/common';
import { askConfirm } from '../ui/confirm';
import { Icon } from '../ui/Icon';
import { reportPendingChanges } from './unsaved-changes';
import { useLoad } from './use-master-data';

let counter = 0;
const newKey = () => {
  counter += 1;
  return `k${Date.now().toString(36)}${String(counter)}`;
};

/** Rooms in order of first appearance, with their checks. */
function roomsOf(items: readonly MasterChecklistItem[]) {
  const rooms = new Map<string, { optional: boolean; items: MasterChecklistItem[] }>();
  for (const item of items) {
    const room = rooms.get(item.room) ?? { optional: item.roomOptional, items: [] };
    room.items.push(item);
    rooms.set(item.room, room);
  }
  return [...rooms.entries()].map(([room, value]) => ({ room, ...value }));
}

/**
 * "רשימת הבדיקה של סיון": the company-wide inspection checklist (DR-195). A business Super Admin
 * reviews it by room, edits checks, marks rooms that only some apartments have, and publishes a
 * new version. Published versions never change; a project updates to a new version from its
 * setup, and existing handovers keep their own checklists.
 */
export function MasterChecklistEditor() {
  const id = useId();
  const [version, setVersion] = useState(0);
  const [data] = useLoad(`master-checklist:${String(version)}`, () =>
    apiGet('/master-checklist', masterChecklistResponseSchema),
  );
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden')
    return <Problem text="רק מנהל־על עסקי רשאי לנהל את רשימת הבדיקה של החברה." />;
  if (data.state === 'error') return <Problem text="טעינת רשימת הבדיקה נכשלה." />;
  return (
    <Editor
      key={String(data.data.current?.version ?? 0)}
      data={data.data}
      titleId={`${id}-title`}
      onPublished={() => {
        setVersion((v) => v + 1);
      }}
    />
  );
}

function Editor({
  data,
  titleId,
  onPublished,
}: {
  readonly data: MasterChecklistResponse;
  readonly titleId: string;
  readonly onPublished: () => void;
}) {
  const start = data.current?.items ?? data.proposal;
  const [items, setItems] = useState<MasterChecklistItem[]>(start);
  const [note, setNote] = useState(data.current === null ? 'גרסה ראשונה של רשימת הבדיקה' : '');
  const [newRoom, setNewRoom] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const dirty = data.current === null || JSON.stringify(items) !== JSON.stringify(start);
  const published = data.current !== null;
  useEffect(() => {
    reportPendingChanges(published && dirty ? 1 : 0);
    return () => {
      reportPendingChanges(0);
    };
  }, [dirty, published]);
  const rooms = roomsOf(items);
  const next = (data.current?.version ?? 0) + 1;
  const update = (key: string, change: Partial<MasterChecklistItem>) => {
    setItems((list) => list.map((i) => (i.key === key ? { ...i, ...change } : i)));
  };
  const setRoomOptional = (room: string, optional: boolean) => {
    setItems((list) => list.map((i) => (i.room === room ? { ...i, roomOptional: optional } : i)));
  };
  const addCheck = (room: string, optional: boolean) => {
    setItems((list) => {
      const last = list.map((i) => i.room).lastIndexOf(room);
      const item: MasterChecklistItem = {
        key: newKey(),
        room,
        roomOptional: optional,
        category: 'כללי',
        component: 'כללי',
        name: '',
      };
      return [...list.slice(0, last + 1), item, ...list.slice(last + 1)];
    });
  };
  const publish = () => {
    const empty = items.find(
      (i) => i.name.trim() === '' || i.category.trim() === '' || i.component.trim() === '',
    );
    if (empty !== undefined) {
      setMessage({ kind: 'error', text: `בחדר "${empty.room}" יש בדיקה עם שדה ריק.` });
      return;
    }
    void askConfirm({
      title: `לפרסם את גרסה ${String(next)}?`,
      message:
        'הגרסה תהיה זמינה לכל הפרויקטים: פרויקטים חדשים יתחילו ממנה, וקיימים יוכלו להתעדכן אליה במסך הקמת הפרויקט. מסירות קיימות וסגורות לא ישתנו. אחרי הפרסום אי אפשר לשנות את הגרסה, רק לפרסם גרסה חדשה.',
      confirmLabel: `פרסום גרסה ${String(next)}`,
    }).then((yes) => {
      if (!yes) return;
      setBusy(true);
      setMessage(null);
      apiPost(
        '/master-checklist',
        {
          basedOn: data.current?.version ?? null,
          note: note.trim(),
          items: items.map((i) => ({
            ...i,
            name: i.name.trim(),
            category: i.category.trim(),
            component: i.component.trim(),
          })),
        },
        masterChecklistResponseSchema,
      )
        .then(() => {
          reportPendingChanges(0);
          onPublished();
        })
        .catch((e: unknown) => {
          const code =
            e instanceof ApiError
              ? (e.body['conflict'] as { code?: string } | undefined)?.code
              : null;
          setMessage({
            kind: 'error',
            text:
              e instanceof NetworkError
                ? 'אין חיבור לשרת. הגרסה לא פורסמה.'
                : code === 'stale'
                  ? 'מישהו פרסם גרסה חדשה בינתיים. רעננו את הדף לפני הפרסום.'
                  : e instanceof ApiError && e.status === 422
                    ? 'יש בדיקות כפולות באותו חדר ותחום, או שדות לא תקינים.'
                    : 'הפרסום נכשל.',
          });
        })
        .finally(() => {
          setBusy(false);
        });
    });
  };

  return (
    <section className="master-checklist" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-title">
        רשימת הבדיקה של סיון
      </h2>
      <p className="hint">
        רשימה אחת לכל הפרויקטים של החברה, לפי חדרים ורכיבים. פרויקט חדש מתחיל ממנה אוטומטית, ובכל
        פרויקט אפשר לכבות בדיקה או להוסיף בדיקה משלו כשצריך. אלה בדיקות חזותיות ותפקודיות בזמן
        המסירה, ולא בדיקה הנדסית או מעבדתית מוסמכת.
      </p>
      {data.current === null ? (
        <p className="notice notice--info">
          עדיין לא פורסמה רשימה. לפניכם הצעת סיון לגרסה 1 ({items.length} בדיקות ב-{rooms.length}{' '}
          חדרים): עברו עליה, התאימו ופרסמו.
        </p>
      ) : (
        <p className="notice notice--success">
          גרסה {data.current.version} בתוקף · {data.current.note}
        </p>
      )}
      <div className="master-checklist__summary" aria-label="סיכום">
        <span>
          <b>{rooms.filter((r) => !r.optional).length}</b> חדרים בכל דירה
        </span>
        <span>
          <b>{rooms.filter((r) => r.optional).length}</b> חדרים לפי דירה
        </span>
        <span>
          <b>{items.length}</b> בדיקות
        </span>
      </div>
      <datalist id={`${titleId}-categories`}>
        {[...new Set(items.map((i) => i.category))].map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <div className="master-checklist__rooms">
        {rooms.map((r) => (
          <details key={r.room} className="master-checklist__room">
            <summary>
              <b>{r.room}</b>
              <span className="hint">
                {r.items.length} בדיקות · {r.optional ? 'רק בחלק מהדירות' : 'בכל דירה'}
              </span>
            </summary>
            <label className="choice">
              <input
                type="checkbox"
                checked={r.optional}
                onChange={(e) => {
                  setRoomOptional(r.room, e.target.checked);
                }}
              />
              <span>חדר שקיים רק בחלק מהדירות (המפקח מוסיף אותו לדירה)</span>
            </label>
            <ul className="master-checklist__items">
              {r.items.map((item) => (
                <li key={item.key} className="master-checklist__item">
                  <input
                    aria-label={`בדיקה: ${item.name || 'חדשה'}`}
                    value={item.name}
                    maxLength={80}
                    placeholder="מה בודקים"
                    onChange={(e) => {
                      update(item.key, { name: e.target.value });
                    }}
                  />
                  <input
                    aria-label="רכיב"
                    value={item.component}
                    maxLength={80}
                    onChange={(e) => {
                      update(item.key, { component: e.target.value });
                    }}
                  />
                  <input
                    aria-label="תחום"
                    list={`${titleId}-categories`}
                    value={item.category}
                    maxLength={80}
                    onChange={(e) => {
                      update(item.key, { category: e.target.value });
                    }}
                  />
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`הסרת הבדיקה: ${item.name}`}
                    onClick={() => {
                      setItems((list) => list.filter((i) => i.key !== item.key));
                    }}
                  >
                    <Icon name="x" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="button"
              onClick={() => {
                addCheck(r.room, r.optional);
              }}
            >
              <Icon name="plus" /> בדיקה בחדר הזה
            </button>
          </details>
        ))}
      </div>
      <form
        className="master-checklist__add-room"
        onSubmit={(e) => {
          e.preventDefault();
          const room = newRoom.trim();
          if (room === '' || rooms.some((r) => r.room === room)) return;
          setItems((list) => [
            ...list,
            {
              key: newKey(),
              room,
              roomOptional: true,
              category: 'כללי',
              component: 'כללי',
              name: '',
            },
          ]);
          setNewRoom('');
        }}
      >
        <label className="field">
          <span>חדר חדש</span>
          <input
            value={newRoom}
            maxLength={80}
            onChange={(e) => {
              setNewRoom(e.target.value);
            }}
          />
        </label>
        <button type="submit" className="button">
          הוספת חדר
        </button>
      </form>
      <label className="field">
        <span>מה השתנה בגרסה הזו</span>
        <input
          value={note}
          maxLength={500}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </label>
      {message !== null && (
        <p className={`notice notice--${message.kind}`} role="alert">
          {message.text}
        </p>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || !dirty || note.trim().length < 3}
          onClick={publish}
        >
          {busy ? 'מפרסם…' : `פרסום גרסה ${String(next)}`}
        </button>
      </div>
    </section>
  );
}
